/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  FlatList,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  useWindowDimensions,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useShellScreenHeaderRefresh } from '../../shell/navigation/TabScreenRefreshContext';
import { navigateInShell } from '../../shell/navigation/rootNavigation';
import { useSessionStore } from '../../shell/session/store/useSessionStore'
import { useModulePermission } from '../../shell/session/hooks/useModulePermission';
import { colors } from '../../shared/theme/colors'
import { ticketColors } from '../../shared/theme/ticketColors'
import { spacing } from '../../shared/theme/spacing'
import { typography } from '../../shared/theme/typography';
import { CONTENT_WIDE_BREAKPOINT, WEB_SIDEBAR_BREAKPOINT } from '../../shared/layout/breakpoints';
import { ShellContentFrame } from '../../shared/layout/ShellContentFrame';
import { ticketSurfaces } from '../../shared/ticket/ticketSurfaces.styles';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { showInfo, showSuccess } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm'
import { useScreenRefresh } from '../../shared/ui/hooks-screen/useScreenRefresh';
import { StatusPill, type StatusPillTone } from '../../shared/surface-cards/StatusPill';
import { ModalShell } from '../../shared/modals/ModalShell'
import { ModalFormField } from '../../shared/modals/ModalFormField'
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { InventoryIsoDateField } from '../../shared/ui/InventoryIsoDateField';
import { HmTimeField } from '../../shared/ui/HmTimeField';
import { formatDateLabelEsAr, formatIsoDate } from '@shared/vueltoDateTime';
import { formatArPhoneDisplay } from '@shared/phone';
import type { DiningTable, Reservation, ReservationStatus } from '../domain/hospitalityTypes';
import {
  groupReservationsByDay,
  isReservationPast,
  splitReservationsByTime,
} from '../domain/reservationSchedule';
import {
  deleteReservation,
  fetchHospitalitySnapshot,
  getTableBookingSettings,
  listReservations,
  seatReservation,
  type TableBookingSettings,
  updateTableBookingSettings,
  upsertReservation,
} from '../infra/hospitalityRepo';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { useReservationsLiveRefresh } from '../hooks/useReservationsLiveRefresh';

const RESERVATION_STATUS_LABELS: Record<ReservationStatus, string> = {
  pending: 'Pendiente',
  confirmed: 'Confirmada',
  seated: 'Sentado',
  cancelled: 'Cancelada',
  no_show: 'No asistió',
};

const RESERVATION_STATUS_TONES: Record<ReservationStatus, StatusPillTone> = {
  pending: 'warning',
  confirmed: 'success',
  seated: 'service',
  cancelled: 'neutral',
  no_show: 'danger',
};

const DEFAULT_BOOKING_SETTINGS: TableBookingSettings = {
  tableBookingEnabled: false,
  tableBookingHorizonDays: 14,
  tableBookingSlotMinutes: 30,
  tableBookingMinLeadHours: 2,
  tableBookingCancelHours: 2,
  tableBookingDefaultDuration: 90,
  tableBookingOpenTime: '11:00',
  tableBookingCloseTime: '23:00',
  tableBookingMinPartySize: 1,
  tableBookingDefaultPartySize: 2,
  tableBookingMaxPartySize: null,
  tableBookingAutoConfirm: true,
  tableBookingRequirePhone: true,
  tableBookingRequireNotes: false,
  tableBookingPaymentMode: 'none',
  tableBookingDepositAmount: null,
  tableBookingDepositPercent: null,
  tableBookingUnpaidExpireMinutes: 30,
};

type ReservationTimeFilter = 'upcoming' | 'past' | 'all';

/** Fila de la lista: encabezado de día o fila de cards (hasta `columns`). */
type ReservationListRow =
  | { type: 'header'; key: string; label: string }
  | { type: 'cards'; key: string; items: Reservation[] };

function reservationDayLabel(day: string, now = new Date()): string {
  if (day === 'sin-fecha') return 'Sin fecha';
  const todayKey = formatIsoDate(now);
  if (day === todayKey) return 'Hoy';
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (day === formatIsoDate(tomorrow)) return 'Mañana';
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (day === formatIsoDate(yesterday)) return 'Ayer';
  return formatDateLabelEsAr(day);
}

export default function ReservationsScreen() {
  const navigation = useNavigation();
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const canOperate = useModulePermission('Tables', 'operate');
  const tablesGate = useCapabilityGate('tables', {
    title: 'Reservas no disponibles',
    subtitle: 'Este rubro no tiene mesas.',
  });
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const { width } = useWindowDimensions();
  const isWide = width >= CONTENT_WIDE_BREAKPOINT;
  const isModalDesktop = width >= WEB_SIDEBAR_BREAKPOINT;

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [timeFilter, setTimeFilter] = useState<ReservationTimeFilter>('upcoming');
  const { upcoming: upcomingReservations, past: pastReservations } = useMemo(
    () => splitReservationsByTime(reservations),
    [reservations],
  );
  /** Headers de día + filas de cards (2 por fila en wide) para el FlatList. */
  const listRows = useMemo<ReservationListRow[]>(() => {
    const rows: ReservationListRow[] = [];
    const cols = isWide ? 2 : 1;
    const pushGroups = (list: Reservation[]) => {
      for (const group of groupReservationsByDay(list)) {
        rows.push({ type: 'header', key: `h:${group.day}`, label: reservationDayLabel(group.day) });
        for (let i = 0; i < group.items.length; i += cols) {
          const items = group.items.slice(i, i + cols);
          rows.push({ type: 'cards', key: `r:${items.map((r) => r.id).join('+')}`, items });
        }
      }
    };
    if (timeFilter === 'upcoming') {
      pushGroups(upcomingReservations);
    } else if (timeFilter === 'past') {
      pushGroups(pastReservations);
    } else {
      pushGroups(upcomingReservations);
      if (pastReservations.length > 0) {
        rows.push({ type: 'header', key: 'h:pasadas', label: 'Pasadas' });
        pushGroups(pastReservations);
      }
    }
    return rows;
  }, [isWide, pastReservations, timeFilter, upcomingReservations]);
  const [tables, setTables] = useState<DiningTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<Reservation | null>(null);
  const [form, setForm] = useState({
    customerName: '',
    customerPhone: '',
    partySize: '2',
    reservationDate: '',
    reservationTime: '',
    durationMinutes: '90',
    diningTableId: '',
    notes: '',
    status: 'confirmed' as ReservationStatus,
  });
  const [saving, setSaving] = useState(false);
  const [bookingSettings, setBookingSettings] = useState<TableBookingSettings>(DEFAULT_BOOKING_SETTINGS);
  const [togglingOnline, setTogglingOnline] = useState(false);
  const [savingBookingSettings, setSavingBookingSettings] = useState(false);

  const loadReservations = useCallback(async () => {
    if (!currentBranchId) return;
    try {
      const [data, settings, snapshot] = await Promise.all([
        listReservations(currentBranchId),
        getTableBookingSettings(currentBranchId),
        fetchHospitalitySnapshot(currentBranchId),
      ]);
      setReservations(data);
      setTables(snapshot.diningTables);
      setBookingSettings(settings ?? DEFAULT_BOOKING_SETTINGS);
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'reservations.load', error, userMessage: 'No se pudieron cargar las reservas.' });
    } finally {
      setLoading(false);
    }
  }, [currentBranchId]);

  const loadReservationsSilent = useCallback(async () => {
    try {
      await loadReservations();
    } catch {
      // Live poll/realtime: no toast spam
    }
  }, [loadReservations]);

  useFocusEffect(
    useCallback(() => {
      void loadReservations();
    }, [loadReservations]),
  );

  useReservationsLiveRefresh(currentBranchId, () => void loadReservationsSilent(), {
    enabled: tablesGate.allowed !== false,
  });

  const { refreshing, onRefresh } = useScreenRefresh(async () => {
    await loadReservations();
  });

  useShellScreenHeaderRefresh('Reservations', onRefresh);

  const openNew = () => {
    setEditing(null);
    const today = new Date().toISOString().slice(0, 10);
    setForm({
      customerName: '',
      customerPhone: '',
      partySize: '2',
      reservationDate: today,
      reservationTime: '20:00',
      durationMinutes: '90',
      diningTableId: '',
      notes: '',
      status: 'confirmed',
    });
    setEditOpen(true);
  };

  // Deep link Inicio → «Nueva reserva»: espera a canOperate (permisos resuelven async).
  const route = useRoute();
  useEffect(() => {
    const params = route.params as { openNew?: boolean } | undefined;
    if (params?.openNew !== true || !canOperate) return;
    navigation.setParams({ openNew: undefined } as never);
    openNew();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- openNew es estable en intención; el param ya fue consumido.
  }, [canOperate, navigation, route.params]);

  const openEdit = (r: Reservation) => {
    setEditing(r);
    setForm({
      customerName: r.customerName,
      customerPhone: r.customerPhone ?? '',
      partySize: String(r.partySize),
      reservationDate: r.reservationDate,
      reservationTime: r.reservationTime,
      durationMinutes: String(r.durationMinutes),
      diningTableId: r.diningTableId ?? '',
      notes: r.notes ?? '',
      status: r.status,
    });
    setEditOpen(true);
  };

  // El slot armado en el form ya venció (inicio + duración): no se puede sentar.
  const formSlotIsPast = isReservationPast({
    reservationDate: form.reservationDate,
    reservationTime: form.reservationTime,
    durationMinutes: Number(form.durationMinutes) || 0,
  });

  const handleSave = async () => {
    if (!currentBranchId) return;
    if (!form.customerName.trim()) {
      showInfo('Faltan datos', 'Completá el nombre del cliente');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(form.reservationDate)) {
      showInfo('Fecha inválida', 'Elegí una fecha del calendario');
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(form.reservationTime)) {
      showInfo('Hora inválida', 'Elegí una hora');
      return;
    }
    const partySize = Number(form.partySize);
    const durationMinutes = Number(form.durationMinutes);
    if (!Number.isFinite(partySize) || partySize < 1 || partySize > 999) {
      showInfo('Datos inválidos', 'La cantidad de personas debe ser entre 1 y 999');
      return;
    }
    if (!Number.isFinite(durationMinutes) || durationMinutes < 1 || durationMinutes > 1440) {
      showInfo('Datos inválidos', 'La duración debe ser entre 1 min y 24 hs');
      return;
    }
    const nextStatus = form.status as ReservationStatus;
    if (nextStatus === 'seated' && !form.diningTableId) {
      showInfo('Asigná una mesa', 'Para sentar la reserva, elegí una mesa primero.');
      return;
    }
    if (nextStatus === 'seated' && editing?.status !== 'seated' && formSlotIsPast) {
      showInfo('Turno vencido', 'El horario de esta reserva ya pasó. Marcala como "No asistió" o cambiale la fecha.');
      return;
    }
    setSaving(true);
    try {
      const reservationId = await upsertReservation({
        reservationId: editing?.id ?? null,
        branchId: currentBranchId,
        customerName: form.customerName.trim(),
        customerPhone: form.customerPhone.trim() || null,
        partySize,
        reservationDate: form.reservationDate,
        reservationTime: form.reservationTime,
        durationMinutes,
        diningTableId: form.diningTableId || null,
        notes: form.notes.trim() || null,
        // Persist fields first; seat_reservation sets seated + opens session atomically.
        status: nextStatus === 'seated' ? (editing?.status === 'seated' ? 'seated' : 'confirmed') : nextStatus,
      });
      if (nextStatus === 'seated') {
        const id = reservationId ?? editing?.id;
        if (!id) throw new Error('reservation_not_found');
        await seatReservation(id);
      }
      await loadReservations();
      setEditOpen(false);
      showSuccess(
        nextStatus === 'seated' ? 'Cliente sentado' : 'Reserva guardada',
        nextStatus === 'seated' ? 'Se abrió la cuenta en la mesa.' : editing ? 'Reserva actualizada' : 'Reserva creada',
      );
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes('reservation_needs_table')) {
        showInfo('Asigná una mesa', 'Para sentar la reserva, elegí una mesa primero.');
      } else {
        presentUnexpectedFailure({ module: 'hospitality', op: 'reservations.save', error, userMessage: 'No se pudo guardar la reserva.' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r: Reservation) => {
    const ok = await confirm({
      title: 'Eliminar reserva',
      message: `¿Seguro que querés eliminar la reserva de ${r.customerName}?`,
      confirmLabel: 'Eliminar',
      destructive: true,
      iconName: 'trash-outline',
    });
    if (!ok) return;
    try {
      await deleteReservation(r.id);
      await loadReservations();
      showSuccess('Reserva eliminada', '');
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'reservations.delete', error, userMessage: 'No se pudo eliminar la reserva.' });
    }
  };

  const handleToggleOnlineBooking = async () => {
    if (!currentBranchId) return;
    setTogglingOnline(true);
    try {
      const nextSettings = {
        ...bookingSettings,
        tableBookingEnabled: !bookingSettings.tableBookingEnabled,
      };
      await updateTableBookingSettings(currentBranchId, {
        tableBookingEnabled: nextSettings.tableBookingEnabled,
      });
      setBookingSettings(nextSettings);
      showSuccess(
        nextSettings.tableBookingEnabled ? 'Reservas online activadas' : 'Reservas online desactivadas',
        nextSettings.tableBookingEnabled
          ? 'Los clientes pueden reservar mesas desde el portal.'
          : '',
      );
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'reservations.toggleOnline', error, userMessage: 'No se pudo cambiar la configuración.' });
    } finally {
      setTogglingOnline(false);
    }
  };

  const handleSaveBookingSettings = async () => {
    if (!currentBranchId) return;
    const positiveFields: Array<[keyof TableBookingSettings, string]> = [
      ['tableBookingHorizonDays', 'Anticipación máxima'],
      ['tableBookingSlotMinutes', 'Duración del turno'],
      ['tableBookingDefaultDuration', 'Duración de la reserva'],
      ['tableBookingMinPartySize', 'Mínimo de personas'],
      ['tableBookingDefaultPartySize', 'Personas por defecto'],
    ];
    const nonNegativeFields: Array<[keyof TableBookingSettings, string]> = [
      ['tableBookingMinLeadHours', 'Anticipación mínima'],
      ['tableBookingCancelHours', 'Cancelación'],
      ['tableBookingUnpaidExpireMinutes', 'Expiración sin pago'],
    ];
    const hasInvalidNumber = [...positiveFields, ...nonNegativeFields].some(([key], index) => {
      const value = bookingSettings[key];
      return typeof value !== 'number' || !Number.isInteger(value) || value < (index < positiveFields.length ? 1 : 0);
    });
    if (hasInvalidNumber) {
      showInfo('Reglas inválidas', 'Usá números enteros. Los tiempos y duraciones deben ser mayores a cero.');
      return;
    }
    if (
      bookingSettings.tableBookingMinPartySize < 1 ||
      bookingSettings.tableBookingMinPartySize > 20 ||
      bookingSettings.tableBookingDefaultPartySize < bookingSettings.tableBookingMinPartySize ||
      bookingSettings.tableBookingDefaultPartySize > 20
    ) {
      showInfo('Personas inválidas', 'Mínimo y default entre 1 y 20, y el default ≥ mínimo.');
      return;
    }
    if (
      bookingSettings.tableBookingMaxPartySize != null &&
      (bookingSettings.tableBookingMaxPartySize < bookingSettings.tableBookingMinPartySize ||
        bookingSettings.tableBookingMaxPartySize > 20)
    ) {
      showInfo('Máximo inválido', 'El máximo tiene que ser ≥ mínimo y hasta 20. Vacío = capacidad de tus mesas.');
      return;
    }
    if (
      bookingSettings.tableBookingPaymentMode === 'deposit' ||
      bookingSettings.tableBookingPaymentMode === 'full'
    ) {
      const hasAmount =
        bookingSettings.tableBookingDepositAmount != null && bookingSettings.tableBookingDepositAmount > 0;
      if (!hasAmount) {
        showInfo('Monto incompleto', 'Poné un monto en pesos. En mesa el % solo no alcanza.');
        return;
      }
    }
    if (!/^\d{2}:\d{2}$/.test(bookingSettings.tableBookingOpenTime)
      || !/^\d{2}:\d{2}$/.test(bookingSettings.tableBookingCloseTime)) {
      showInfo('Horario inválido', 'La ventana de respaldo usa formato HH:MM.');
      return;
    }
    setSavingBookingSettings(true);
    try {
      await updateTableBookingSettings(currentBranchId, {
        ...bookingSettings,
        clearMaxPartySize: bookingSettings.tableBookingMaxPartySize == null,
      });
      showSuccess('Reglas guardadas', 'Las reglas de reserva online quedaron actualizadas.');
    } catch (error) {
      presentUnexpectedFailure({ module: 'hospitality', op: 'reservations.saveBookingSettings', error, userMessage: 'No se pudieron guardar las reglas.' });
    } finally {
      setSavingBookingSettings(false);
    }
  };

  const handleReservationStatus = async (reservation: Reservation, status: ReservationStatus) => {
    try {
      if (status === 'seated') {
        if (!reservation.diningTableId) {
          showInfo('Asigná una mesa', 'Para sentar la reserva, elegí una mesa primero.');
          return;
        }
        await seatReservation(reservation.id);
        await loadReservations();
        showSuccess('Cliente sentado', 'Se abrió la cuenta en la mesa.');
        return;
      }
      await upsertReservation({
        reservationId: reservation.id,
        branchId: reservation.branchId,
        diningTableId: reservation.diningTableId ?? null,
        customerName: reservation.customerName,
        customerPhone: reservation.customerPhone ?? null,
        partySize: reservation.partySize,
        reservationDate: reservation.reservationDate,
        reservationTime: reservation.reservationTime,
        durationMinutes: reservation.durationMinutes,
        notes: reservation.notes ?? null,
        status,
      });
      await loadReservations();
      showSuccess('Reserva actualizada', RESERVATION_STATUS_LABELS[status]);
    } catch (error) {
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes('reservation_needs_table')) {
        showInfo('Asigná una mesa', 'Para sentar la reserva, elegí una mesa primero.');
      } else {
        presentUnexpectedFailure({ module: 'hospitality', op: 'reservations.updateStatus', error, userMessage: 'No se pudo actualizar la reserva.' });
      }
    }
  };

  if (!tablesGate.allowed) {
    return (
      <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
        {tablesGate.blocked}
      </ShellContentFrame>
    );
  }

  return (
    <ShellContentFrame style={[styles.shell, !isWide && styles.shellCompact]}>
      {ConfirmModal}
      <FlatList
        data={listRows}
        keyExtractor={(row) => row.key}
        contentContainerStyle={styles.listContent}
        refreshing={refreshing}
        onRefresh={onRefresh}
        ListHeaderComponent={
          <View style={styles.listHeader}>
            <View style={styles.hero}>
              <View style={[styles.heroRow, !isWide && styles.heroRowStack]}>
                <View style={styles.heroCopy}>
                  <Text style={styles.heroKicker}>Salón</Text>
                  <Text style={styles.heroTitle}>Reservas</Text>
                  <Text style={styles.heroSub}>Turnos del salón.</Text>
                </View>
                {canOperate ? (
                  <TouchableOpacity
                    style={[styles.heroCta, !isWide && styles.heroCtaFull]}
                    onPress={openNew}
                    accessibilityRole="button"
                    accessibilityLabel="Nueva reserva"
                  >
                    <Ionicons name="add" size={18} color={colors.surface} />
                    <Text style={styles.heroCtaText}>Nueva reserva</Text>
                  </TouchableOpacity>
                ) : null}
              </View>
              {reservations.length > 0 ? (
                <Text style={styles.heroMeta}>
                  {reservations.length} reserva{reservations.length === 1 ? '' : 's'}
                </Text>
              ) : null}
            </View>

            {canOperate ? (
              <View style={styles.onlineCard}>
                <View style={styles.onlineCardInfo}>
                  <Text style={styles.onlineCardTitle}>Reservas online</Text>
                  <Text style={styles.onlineCardDesc}>
                    {bookingSettings.tableBookingEnabled
                      ? 'Los clientes pueden reservar mesas desde tu tienda online.'
                      : 'Activá para que los clientes reserven mesas desde tu tienda online.'}
                  </Text>
                </View>
                <Switch
                  value={bookingSettings.tableBookingEnabled}
                  onValueChange={() => void handleToggleOnlineBooking()}
                  disabled={togglingOnline}
                  trackColor={{ false: colors.borderSoft, true: colors.success }}
                  thumbColor={colors.surface}
                  accessibilityLabel="Reservas online"
                />
              </View>
            ) : null}
            {canOperate && bookingSettings.tableBookingEnabled ? (
              <View style={styles.bookingRulesCard}>
                <Text style={styles.bookingRulesTitle}>Reglas de reserva</Text>
                <Text style={styles.bookingRulesDesc}>
                  Todo esto sale en tu tienda online. Los horarios del día los definís en Tienda online.
                </Text>
                <TouchableOpacity
                  style={styles.hoursLink}
                  onPress={() => navigateInShell(navigation, 'StorefrontHub')}
                  accessibilityRole="button"
                  accessibilityLabel="Ir a Tienda online"
                >
                  <Ionicons name="time-outline" size={16} color={ticketColors.gold} />
                  <Text style={styles.hoursLinkText}>Horarios y cierres del local</Text>
                </TouchableOpacity>
                <View style={styles.rulesGrid}>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Reservar hasta (días antes)</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingHorizonDays)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingHorizonDays: Number(value) }))} keyboardType="numeric" accessibilityLabel="Reservar hasta cuántos días antes" />
                    <Text style={styles.ruleHint}>Ej.: 14 → reservan hasta 2 semanas antes.</Text>
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Cada cuánto abre un turno (min)</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingSlotMinutes)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingSlotMinutes: Number(value) }))} keyboardType="numeric" accessibilityLabel="Cada cuántos minutos abre un turno" />
                    <Text style={styles.ruleHint}>Ej.: 30 → turnos 20:00, 20:30, 21:00.</Text>
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Anticipación mínima (hs)</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingMinLeadHours)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingMinLeadHours: Number(value) }))} keyboardType="numeric" accessibilityLabel="Anticipación mínima en horas" />
                    <Text style={styles.ruleHint}>Ej.: 2 → no toman reservas para dentro de 2 horas.</Text>
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Cancelación hasta (hs antes)</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingCancelHours)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingCancelHours: Number(value) }))} keyboardType="numeric" accessibilityLabel="Cancelación hasta cuántas horas antes" />
                    <Text style={styles.ruleHint}>Ej.: 2 → cancelan hasta 2 horas antes del turno.</Text>
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Duración del turno (min)</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingDefaultDuration)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingDefaultDuration: Number(value) }))} keyboardType="numeric" accessibilityLabel="Duración del turno en minutos" />
                    <Text style={styles.ruleHint}>Ej.: 90 → cada reserva ocupa la mesa 1 h 30.</Text>
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Mínimo de personas</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingMinPartySize)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingMinPartySize: Number(value) }))} keyboardType="numeric" accessibilityLabel="Mínimo de personas" />
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Personas por defecto</Text>
                    <TextInput style={modalExperience.formInput} value={String(bookingSettings.tableBookingDefaultPartySize)} onChangeText={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingDefaultPartySize: Number(value) }))} keyboardType="numeric" accessibilityLabel="Personas por defecto" />
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Máximo de personas</Text>
                    <TextInput
                      style={modalExperience.formInput}
                      value={bookingSettings.tableBookingMaxPartySize == null ? '' : String(bookingSettings.tableBookingMaxPartySize)}
                      onChangeText={(value) => {
                        const trimmed = value.trim();
                        setBookingSettings((settings) => ({
                          ...settings,
                          tableBookingMaxPartySize: trimmed === '' ? null : Number(trimmed),
                        }));
                      }}
                      keyboardType="numeric"
                      placeholder="Auto"
                      accessibilityLabel="Máximo de personas"
                    />
                    <Text style={styles.ruleHint}>Vacío = se usa la capacidad de tus mesas.</Text>
                  </View>
                  <View style={styles.ruleNote}>
                    <Text style={styles.ruleHint}>
                      Horario de respaldo — se usa solo si el local no tiene horarios cargados.
                    </Text>
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Reserva online abre</Text>
                    <HmTimeField value={bookingSettings.tableBookingOpenTime} onChange={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingOpenTime: value }))} accessibilityLabel="Horario de apertura de reserva online" comfortable />
                  </View>
                  <View style={styles.ruleField}>
                    <Text style={styles.ruleLabel}>Reserva online cierra</Text>
                    <HmTimeField value={bookingSettings.tableBookingCloseTime} onChange={(value) => setBookingSettings((settings) => ({ ...settings, tableBookingCloseTime: value }))} accessibilityLabel="Horario de cierre de reserva online" comfortable />
                  </View>
                </View>

                <View style={styles.ruleSwitchRow}>
                  <View style={styles.ruleSwitchCopy}>
                    <Text style={styles.ruleLabel}>Confirmar automático</Text>
                    <Text style={styles.bookingRulesDesc}>Si está apagado, la reserva queda pendiente.</Text>
                  </View>
                  <Switch
                    value={bookingSettings.tableBookingAutoConfirm}
                    onValueChange={(value) => setBookingSettings((s) => ({ ...s, tableBookingAutoConfirm: value }))}
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                  />
                </View>
                <View style={styles.ruleSwitchRow}>
                  <View style={styles.ruleSwitchCopy}>
                    <Text style={styles.ruleLabel}>Pedir teléfono</Text>
                  </View>
                  <Switch
                    value={bookingSettings.tableBookingRequirePhone}
                    onValueChange={(value) => setBookingSettings((s) => ({ ...s, tableBookingRequirePhone: value }))}
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                  />
                </View>
                <View style={styles.ruleSwitchRow}>
                  <View style={styles.ruleSwitchCopy}>
                    <Text style={styles.ruleLabel}>Pedir notas</Text>
                  </View>
                  <Switch
                    value={bookingSettings.tableBookingRequireNotes}
                    onValueChange={(value) => setBookingSettings((s) => ({ ...s, tableBookingRequireNotes: value }))}
                    trackColor={{ false: colors.borderSoft, true: colors.success }}
                    thumbColor={colors.surface}
                  />
                </View>

                <Text style={[styles.ruleLabel, { marginTop: spacing.sm }]}>Cobro al reservar</Text>
                <View style={styles.paymentModeRow}>
                  {([
                    { id: 'none' as const, label: 'Sin cobro' },
                    { id: 'deposit' as const, label: 'Seña' },
                    { id: 'full' as const, label: 'Cobro total' },
                  ]).map((opt) => {
                    const selected = bookingSettings.tableBookingPaymentMode === opt.id;
                    return (
                      <TouchableOpacity
                        key={opt.id}
                        style={[styles.paymentChip, selected && styles.paymentChipSelected]}
                        onPress={() => setBookingSettings((s) => ({ ...s, tableBookingPaymentMode: opt.id }))}
                      >
                        <Text style={[styles.paymentChipText, selected && styles.paymentChipTextSelected]}>{opt.label}</Text>
                      </TouchableOpacity>
                    );
                  })}
                </View>
                {bookingSettings.tableBookingPaymentMode !== 'none' ? (
                  <View style={styles.rulesGrid}>
                    <View style={styles.ruleField}>
                      <Text style={styles.ruleLabel}>Seña en pesos</Text>
                      <TextInput
                        style={modalExperience.formInput}
                        value={bookingSettings.tableBookingDepositAmount == null ? '' : String(bookingSettings.tableBookingDepositAmount)}
                        onChangeText={(value) => {
                          const trimmed = value.trim();
                          setBookingSettings((s) => ({
                            ...s,
                            tableBookingDepositAmount: trimmed === '' ? null : Number(trimmed),
                          }));
                        }}
                        keyboardType="numeric"
                      />
                    </View>
                    <View style={styles.ruleField}>
                      <Text style={styles.ruleLabel}>…o seña en %</Text>
                      <TextInput
                        style={modalExperience.formInput}
                        value={bookingSettings.tableBookingDepositPercent == null ? '' : String(bookingSettings.tableBookingDepositPercent)}
                        onChangeText={(value) => {
                          const trimmed = value.trim();
                          setBookingSettings((s) => ({
                            ...s,
                            tableBookingDepositPercent: trimmed === '' ? null : Number(trimmed),
                          }));
                        }}
                        keyboardType="numeric"
                      />
                    </View>
                    <View style={styles.ruleField}>
                      <Text style={styles.ruleLabel}>Vence sin pago (min)</Text>
                      <TextInput
                        style={modalExperience.formInput}
                        value={String(bookingSettings.tableBookingUnpaidExpireMinutes)}
                        onChangeText={(value) => setBookingSettings((s) => ({ ...s, tableBookingUnpaidExpireMinutes: Number(value) }))}
                        keyboardType="numeric"
                      />
                    </View>
                  </View>
                ) : null}
                {bookingSettings.tableBookingPaymentMode !== 'none' ? (
                  <Text style={styles.bookingRulesDesc}>
                    Con seña o cobro total la reserva queda pendiente hasta que paguen por Mercado Pago. Poné un monto en pesos (o un % de la cuenta). Si no pagan, la reserva se libera sola a los minutos que configures.
                  </Text>
                ) : null}

                <TouchableOpacity style={[styles.rulesSaveButton, savingBookingSettings && styles.btnDisabled]} onPress={() => void handleSaveBookingSettings()} disabled={savingBookingSettings} accessibilityRole="button" accessibilityLabel="Guardar reglas">
                  <Text style={styles.rulesSaveButtonText}>{savingBookingSettings ? 'Guardando…' : 'Guardar reglas'}</Text>
                </TouchableOpacity>
              </View>
            ) : null}
            {reservations.length > 0 ? (
              <View style={styles.timeFilterRow}>
                {([
                  { key: 'upcoming' as const, label: `Próximas (${upcomingReservations.length})`, a11y: 'Ver reservas próximas' },
                  { key: 'past' as const, label: `Pasadas (${pastReservations.length})`, a11y: 'Ver reservas pasadas' },
                  { key: 'all' as const, label: `Todas (${reservations.length})`, a11y: 'Ver todas las reservas' },
                ]).map((opt) => {
                  const selected = timeFilter === opt.key;
                  return (
                    <TouchableOpacity
                      key={opt.key}
                      style={[styles.filterChip, selected && styles.filterChipSelected]}
                      onPress={() => setTimeFilter(opt.key)}
                      accessibilityRole="button"
                      accessibilityLabel={opt.a11y}
                      accessibilityState={{ selected }}
                    >
                      <Text style={[styles.filterChipLabel, selected && styles.filterChipLabelSelected]}>{opt.label}</Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            ) : null}
          </View>
        }
        ListEmptyComponent={
          loading ? (
            <ActivityIndicator color={colors.accent} style={styles.loader} />
          ) : reservations.length === 0 ? (
            <ModuleEmptyState
              variant="nest"
              kicker="Siguiente paso"
              title="Sin reservas"
              subtitle="Todavía no tenés turnos."
              primaryAction={canOperate ? { label: 'Nueva reserva', onPress: openNew } : undefined}
            />
          ) : (
            <ModuleEmptyState
              variant="nest"
              title={timeFilter === 'past' ? 'Sin reservas pasadas' : 'Sin reservas próximas'}
              subtitle="Cambiá el filtro para ver el resto."
            />
          )
        }
        renderItem={({ item: row }) => {
          if (row.type === 'header') {
            return <Text style={styles.dayHeader}>{row.label}</Text>;
          }
          return (
            <View style={styles.cardRow}>
              {row.items.map((item) => {
                // Turno vencido: no se puede confirmar/sentar — se muestra como "Pasada".
                const past = isReservationPast(item);
                const showPast = past && (item.status === 'pending' || item.status === 'confirmed');
                return (
                  <TouchableOpacity
                    key={item.id}
                    style={styles.card}
                    onPress={() => canOperate && openEdit(item)}
                    disabled={!canOperate}
                    accessibilityRole={canOperate ? 'button' : undefined}
                    accessibilityLabel={`Reserva de ${item.customerName}`}
                  >
                    <View style={styles.cardHeader}>
                      <Text style={styles.cardName} numberOfLines={1}>{item.customerName}</Text>
                      <View style={styles.cardBadges}>
                        {item.source === 'portal' ? <Text style={styles.portalBadge}>Portal</Text> : null}
                        <StatusPill
                          tone={showPast ? 'neutral' : RESERVATION_STATUS_TONES[item.status]}
                          label={showPast ? 'Pasada' : RESERVATION_STATUS_LABELS[item.status]}
                          compact
                        />
                      </View>
                    </View>
                    <View style={styles.cardBody}>
                      <Text style={styles.cardMeta}>
                        {item.reservationTime.slice(0, 5)} · {item.partySize} personas
                      </Text>
                      {item.diningTableId ? <Text style={styles.cardMeta}>Mesa {tables.find((table) => table.id === item.diningTableId)?.name ?? 'asignada'}</Text> : null}
                      {item.customerPhone ? <Text style={styles.cardMeta}>{formatArPhoneDisplay(item.customerPhone)}</Text> : null}
                    </View>
                    {showPast ? <Text style={styles.cardMeta}>El turno ya pasó.</Text> : null}
                    {item.notes ? <Text style={styles.cardNotes}>{item.notes}</Text> : null}
                    {canOperate ? (
                      <View style={styles.cardActions}>
                        {!past && item.status === 'pending' ? (
                          <>
                            <TouchableOpacity style={styles.cardActionButton} onPress={(e) => { e.stopPropagation(); void handleReservationStatus(item, 'confirmed'); }} accessibilityRole="button" accessibilityLabel={`Confirmar reserva de ${item.customerName}`}><Text style={styles.cardActionLabel}>Confirmar</Text></TouchableOpacity>
                            <TouchableOpacity style={styles.cardActionButton} onPress={(e) => { e.stopPropagation(); void handleReservationStatus(item, 'cancelled'); }} accessibilityRole="button" accessibilityLabel={`Rechazar reserva de ${item.customerName}`}><Text style={[styles.cardActionLabel, styles.cardActionDanger]}>Rechazar</Text></TouchableOpacity>
                          </>
                        ) : null}
                        {!past && item.status === 'confirmed' ? (
                          <>
                            <TouchableOpacity style={styles.cardActionButton} onPress={(e) => { e.stopPropagation(); void handleReservationStatus(item, 'seated'); }} accessibilityRole="button" accessibilityLabel={`Sentar ${item.customerName}`}><Text style={styles.cardActionLabel}>Sentar</Text></TouchableOpacity>
                            <TouchableOpacity style={styles.cardActionButton} onPress={(e) => { e.stopPropagation(); void handleReservationStatus(item, 'no_show'); }} accessibilityRole="button" accessibilityLabel={`Marcar que no asistió ${item.customerName}`}><Text style={[styles.cardActionLabel, styles.cardActionDanger]}>No asistió</Text></TouchableOpacity>
                          </>
                        ) : null}
                        <TouchableOpacity style={styles.deleteBtn} onPress={(e) => { e.stopPropagation(); void handleDelete(item); }} accessibilityRole="button" accessibilityLabel={`Eliminar reserva de ${item.customerName}`}><Text style={styles.deleteBtnLabel}>Eliminar</Text></TouchableOpacity>
                      </View>
                    ) : null}
                  </TouchableOpacity>
                );
              })}
            </View>
          );
        }}
      />

      <ModalShell
        visible={editOpen}
        onClose={() => !saving && setEditOpen(false)}
        title={editing ? 'Editar reserva' : 'Nueva reserva'}
        widthVariant="comfortable"
        keyboardAvoiding
        cardStyle={isModalDesktop ? styles.modalWideCard : styles.modalSheetCard}
        scrollContentStyle={[styles.modalContent, isModalDesktop && styles.modalContentWide]}
        footer={
          <View style={styles.footerActions}>
            <TouchableOpacity
              style={[styles.footerBtn, styles.footerBtnSecondary]}
              onPress={() => setEditOpen(false)}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Cancelar"
            >
              <Text style={styles.footerBtnSecondaryText}>Cancelar</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.footerBtn, styles.footerBtnPrimary, saving && styles.btnDisabled]}
              onPress={() => void handleSave()}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Guardar reserva"
            >
              <Text style={styles.footerBtnPrimaryText}>{saving ? 'Guardando…' : 'Guardar'}</Text>
            </TouchableOpacity>
          </View>
        }
      >
        <View style={[styles.formGrid, isModalDesktop && styles.formGridWide]}>
          <View style={styles.formFull}>
            <ModalFormField label="Nombre del cliente" required>
              <TextInput
                style={modalExperience.formInput}
                value={form.customerName}
                onChangeText={(v) => setForm((f) => ({ ...f, customerName: v }))}
                placeholder="Nombre del cliente"
                placeholderTextColor={colors.textMuted}
                accessibilityLabel="Nombre del cliente"
              />
            </ModalFormField>
          </View>
          <View style={styles.formFull}>
            <ModalFormField label="Teléfono">
              <TextInput
                style={modalExperience.formInput}
                value={form.customerPhone}
                onChangeText={(v) => setForm((f) => ({ ...f, customerPhone: v }))}
                placeholder="Opcional"
                placeholderTextColor={colors.textMuted}
                keyboardType="phone-pad"
                accessibilityLabel="Teléfono"
              />
            </ModalFormField>
          </View>
          <View style={styles.formHalf}>
            <ModalFormField label="Fecha" required>
              <InventoryIsoDateField
                value={form.reservationDate}
                onChange={(iso) => setForm((f) => ({ ...f, reservationDate: iso }))}
                emptyLabel="Elegí el día"
                ariaLabel="Fecha de reserva"
                showClearButton={false}
              />
            </ModalFormField>
          </View>
          <View style={styles.formHalf}>
            <ModalFormField label="Hora" required>
              <HmTimeField
                value={form.reservationTime}
                onChange={(hm) => setForm((f) => ({ ...f, reservationTime: hm }))}
                accessibilityLabel="Hora de reserva"
                comfortable
              />
            </ModalFormField>
          </View>
          <View style={styles.formHalf}>
            <ModalFormField label="Personas" required>
              <TextInput
                style={modalExperience.formInput}
                value={form.partySize}
                onChangeText={(v) => setForm((f) => ({ ...f, partySize: v }))}
                placeholder="2"
                placeholderTextColor={colors.textMuted}
                keyboardType="numeric"
                accessibilityLabel="Cantidad de personas"
              />
            </ModalFormField>
          </View>
          <View style={styles.formHalf}>
            <ModalFormField label="Duración" required>
              <TextInput
                style={modalExperience.formInput}
                value={form.durationMinutes}
                onChangeText={(v) => setForm((f) => ({ ...f, durationMinutes: v }))}
                placeholder="90 min"
                placeholderTextColor={colors.textMuted}
                keyboardType="numeric"
                accessibilityLabel="Duración en minutos"
              />
            </ModalFormField>
          </View>
          <View style={styles.formFull}>
            <ModalFormField label="Mesa">
              <View style={styles.tableChipRow}>
                {[{ id: '', label: 'Sin mesa' }, ...tables.filter((table) => table.capacity >= Number(form.partySize || 0) || table.id === form.diningTableId).map((table) => ({ id: table.id, label: `${table.name} · ${table.capacity}` }))].map((table) => {
                  const selected = form.diningTableId === table.id;
                  return <TouchableOpacity key={table.id || 'none'} style={[styles.statusChip, selected && styles.statusChipActive]} onPress={() => setForm((formState) => ({ ...formState, diningTableId: table.id }))} accessibilityRole="radio" accessibilityLabel={table.id ? `Mesa ${table.label}` : table.label} accessibilityState={{ selected }}><Text style={[styles.statusChipLabel, selected && styles.statusChipLabelActive]}>{table.label}</Text></TouchableOpacity>;
                })}
              </View>
            </ModalFormField>
          </View>
          <View style={styles.formFull}>
            <ModalFormField label="Notas">
              <TextInput
                style={[modalExperience.formInput, styles.notesInput]}
                value={form.notes}
                onChangeText={(v) => setForm((f) => ({ ...f, notes: v }))}
                placeholder="Opcional"
                placeholderTextColor={colors.textMuted}
                multiline
                accessibilityLabel="Notas"
              />
            </ModalFormField>
          </View>
          <View style={styles.formFull}>
            <ModalFormField label="Estado">
              <View style={styles.statusRow}>
                {(Object.keys(RESERVATION_STATUS_LABELS) as ReservationStatus[])
                  .filter((status) => status !== 'seated' || !formSlotIsPast || form.status === 'seated')
                  .map((status) => {
                  const selected = form.status === status;
                  return (
                    <TouchableOpacity
                      key={status}
                      style={[styles.statusChip, selected && styles.statusChipActive]}
                      onPress={() => setForm((f) => ({ ...f, status }))}
                      accessibilityRole="radio"
                      accessibilityLabel={RESERVATION_STATUS_LABELS[status]}
                      accessibilityState={{ selected }}
                    >
                      <Text style={[styles.statusChipLabel, selected && styles.statusChipLabelActive]}>
                        {RESERVATION_STATUS_LABELS[status]}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </ModalFormField>
          </View>
        </View>
      </ModalShell>
    </ShellContentFrame>
  );
}

const styles = StyleSheet.create({
  shell: {
    ...ticketSurfaces.screen,
    paddingHorizontal: spacing.lg,
    paddingTop: spacing.lg,
  },
  shellCompact: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
  },
  listContent: {
    paddingBottom: spacing.xl,
    gap: 12,
    flexGrow: 1,
  },
  listHeader: { marginBottom: 4, gap: 12 },
  timeFilterRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  filterChip: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 999,
    paddingHorizontal: spacing.sm,
    paddingVertical: 6,
    backgroundColor: ticketColors.tile,
  },
  filterChipSelected: { borderColor: ticketColors.gold, backgroundColor: ticketColors.brandSoft },
  filterChipLabel: { ...typography.caption, color: ticketColors.ink2 },
  filterChipLabelSelected: { color: ticketColors.brandInk, fontWeight: '700' },
  dayHeader: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontWeight: '800',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginTop: 2,
  },
  cardRow: { flexDirection: 'row', gap: 12 },
  hero: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 12,
    padding: 14,
    gap: 10,
  },
  heroRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  heroRowStack: { flexDirection: 'column', alignItems: 'stretch' },
  heroCopy: { flex: 1, minWidth: 0, gap: 4 },
  heroKicker: {
    ...typography.caption,
    fontSize: 11,
    fontWeight: '700',
    color: colors.accent,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
  },
  heroTitle: {
    fontSize: 22,
    fontWeight: '800',
    letterSpacing: -0.3,
    color: ticketColors.brandInk,
  },
  heroSub: { ...typography.caption, fontSize: 13.5, color: colors.textSecondary, lineHeight: 18 },
  heroMeta: {
    ...typography.caption,
    fontWeight: '700',
    color: colors.accent,
    fontVariant: ['tabular-nums'],
  },
  heroCta: {
    height: 44,
    minWidth: 144,
    paddingHorizontal: 14,
    borderRadius: 12,
    backgroundColor: colors.accent,
    borderWidth: 1,
    borderColor: colors.accent,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    flexShrink: 0,
  },
  heroCtaFull: { alignSelf: 'stretch', minWidth: 0 },
  heroCtaText: { ...typography.body, fontSize: 14, color: colors.surface, fontWeight: '700' },
  loader: { marginTop: spacing.xl },
  card: {
    flex: 1,
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    padding: 14,
    gap: 10,
    minHeight: 128,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: spacing.sm,
  },
  cardName: { fontSize: 15, color: colors.textPrimary, fontWeight: '700', flex: 1, minWidth: 0 },
  cardBadges: { flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 0 },
  portalBadge: {
    ...typography.caption,
    color: colors.accent,
    backgroundColor: ticketColors.tile,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    fontWeight: '700',
    paddingHorizontal: 7,
    paddingVertical: 3,
  },
  cardBody: { flexDirection: 'row', gap: 8, flexWrap: 'wrap' },
  cardMeta: { ...typography.caption, color: colors.textMuted, fontWeight: '600' },
  cardNotes: { ...typography.caption, color: colors.textSecondary, lineHeight: 18 },
  cardActions: { flexDirection: 'row', flexWrap: 'wrap', gap: 10, alignItems: 'center' },
  cardActionButton: { paddingVertical: 2 },
  cardActionLabel: { ...typography.caption, color: colors.accent, fontWeight: '700' },
  cardActionDanger: { color: colors.danger },
  deleteBtn: { alignSelf: 'flex-start', paddingVertical: 2 },
  deleteBtnLabel: { ...typography.caption, color: colors.danger, fontWeight: '600' },
  onlineCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: ticketColors.tile,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    padding: 14,
    gap: spacing.md,
  },
  onlineCardInfo: { flex: 1, minWidth: 0, gap: 2 },
  onlineCardTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '700' },
  onlineCardDesc: { ...typography.caption, color: colors.textSecondary, lineHeight: 18 },
  bookingRulesCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: ticketColors.rule,
    padding: 14,
    gap: 12,
  },
  bookingRulesTitle: { ...typography.body, color: colors.textPrimary, fontWeight: '700' },
  bookingRulesDesc: { ...typography.caption, color: colors.textSecondary, lineHeight: 18 },
  hoursLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    alignSelf: 'flex-start',
    paddingVertical: 4,
  },
  hoursLinkText: { ...typography.caption, color: ticketColors.gold, fontWeight: '700' },
  ruleSwitchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
  },
  ruleSwitchCopy: { flex: 1, minWidth: 0, gap: 2 },
  paymentModeRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  paymentChip: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: ticketColors.tile,
  },
  paymentChipSelected: { backgroundColor: ticketColors.gold, borderColor: ticketColors.gold },
  paymentChipText: { ...typography.caption, color: ticketColors.ink2, fontWeight: '700' },
  paymentChipTextSelected: { color: '#FFFFFF' },
  rulesGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  ruleField: { flexGrow: 1, flexBasis: 180, minWidth: 0, gap: 6 },
  ruleNote: { flexBasis: '100%', minWidth: 0 },
  ruleLabel: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  ruleHint: { ...typography.caption, color: colors.textMuted, fontWeight: '500', lineHeight: 16 },
  rulesSaveButton: {
    alignSelf: 'flex-start',
    minHeight: 40,
    borderRadius: 10,
    backgroundColor: colors.accent,
    justifyContent: 'center',
    paddingHorizontal: 14,
  },
  rulesSaveButtonText: { ...typography.body, color: colors.surface, fontSize: 14, fontWeight: '700' },
  modalWideCard: {
    maxWidth: 640,
    width: '100%',
    borderRadius: 18,
    paddingHorizontal: 20,
    paddingTop: 14,
    paddingBottom: 0,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  modalSheetCard: { paddingHorizontal: 0 },
  modalContent: { paddingHorizontal: 14, paddingTop: 12, paddingBottom: spacing.md },
  modalContentWide: { marginHorizontal: -20, paddingHorizontal: 20 },
  formGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.md },
  formGridWide: { gap: spacing.md },
  formFull: { width: '100%' },
  formHalf: { flexGrow: 1, flexBasis: 140, minWidth: 0 },
  tableChipRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  notesInput: { minHeight: 76, textAlignVertical: 'top' },
  statusRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 6 },
  statusChip: {
    borderWidth: 1,
    borderColor: ticketColors.rule,
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 6,
    backgroundColor: ticketColors.tile,
  },
  statusChipActive: { borderColor: colors.accent, backgroundColor: colors.accent },
  statusChipLabel: { ...typography.caption, color: colors.textSecondary, fontWeight: '700' },
  statusChipLabelActive: { color: colors.surface },
  footerActions: {
    flexDirection: 'row',
    gap: 10,
    paddingTop: 4,
    paddingBottom: 16,
    backgroundColor: colors.surface,
  },
  footerBtn: {
    height: 44,
    flex: 1,
    minWidth: 0,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 18,
  },
  footerBtnSecondary: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: ticketColors.rule,
  },
  footerBtnSecondaryText: { ...typography.body, fontSize: 14, color: colors.textPrimary, fontWeight: '700' },
  footerBtnPrimary: { backgroundColor: colors.accent, borderWidth: 1, borderColor: colors.accent },
  footerBtnPrimaryText: { ...typography.body, fontSize: 14, color: colors.surface, fontWeight: '700' },
  btnDisabled: { opacity: 0.6 },
});
